import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { renderAsync } from 'docx-preview';

interface DocxPreviewProps {
  url: string;
  fileName: string;
}

export const DocxPreview: React.FC<DocxPreviewProps> = ({ url, fileName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const renderDocx = async () => {
    setIsLoading(true);
    setIsError(false);
    setErrorMsg('');
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        await renderAsync(arrayBuffer, containerRef.current, undefined, {
          className: 'docx-preview-container',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          experimental: true,
        });
      }
    } catch (err: any) {
      console.warn('[DocxPreview] Rendering failed:', err);
      setIsError(true);
      setErrorMsg(err.message || 'Failed to render DOCX document');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    renderDocx();
  }, [url]);

  return (
    <div className="flex-1 w-full h-full bg-slate-900 flex flex-col overflow-hidden relative select-text">
      {isLoading && (
        <div className="absolute inset-0 z-10 bg-slate-950/90 backdrop-blur-xs flex flex-col items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-xs font-semibold">Parsing Word document ({fileName})...</p>
        </div>
      )}

      {isError && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto gap-3">
          <AlertCircle className="h-10 w-10 text-amber-400" />
          <p className="font-bold text-foreground text-sm">DOCX Rendering Failed</p>
          <p className="text-xs text-muted-foreground">
            {errorMsg || 'Unable to render Word document preview in browser.'}
          </p>
          <button
            type="button"
            onClick={renderDocx}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition mt-2"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Retry Preview</span>
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className="flex-1 overflow-auto p-4 sm:p-8 flex justify-center bg-slate-950 text-slate-900 shadow-inner"
        style={{
          minHeight: '100%',
        }}
      />
    </div>
  );
};
